import React, { useState, useEffect } from "react";
import type { ImageFile } from "../App";
import { EditModal } from "./EditModal";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, Edit2, Download, RefreshCcw } from "lucide-react";

interface ImagesProps {
  images: ImageFile[];
  onDelete: (id: number) => void;
  onReprocess: (id: number) => void;
}

export function Images({ images, onDelete, onReprocess }: ImagesProps) {
  if (images.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {images.map((image) => (
        <ImageSpot key={image.id} image={image} onDelete={onDelete} onReprocess={onReprocess} />
      ))}
    </div>
  );
}

export function CompareSlider({ before, after }: { before: string; after: string }) {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const container = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const position = ((clientX - container.left) / container.width) * 100;
    setSliderPos(Math.min(100, Math.max(0, position)));
  };

  return (
    <div
      className="relative w-full h-full cursor-col-resize select-none overflow-hidden touch-none"
      onMouseDown={() => setIsDragging(true)}
      onMouseUp={() => setIsDragging(false)}
      onMouseLeave={() => setIsDragging(false)}
      onMouseMove={handleMove}
      onTouchStart={() => setIsDragging(true)}
      onTouchEnd={() => setIsDragging(false)}
      onTouchMove={handleMove}
    >
      <div className="absolute inset-0 bg-white">
        <div className="bg-checkered absolute inset-0 opacity-5 pointer-events-none" />
      </div>

      <div className="absolute inset-0 h-full w-full flex items-center justify-center">
        {after && (
          <img
            draggable="false"
            src={after}
            alt="After"
            className="h-full w-full object-contain p-1"
          />
        )}
        <div className="absolute bottom-4 right-4 px-3 py-1 bg-primary/80 text-white text-[10px] font-black rounded-full uppercase tracking-widest z-0 backdrop-blur-md">After</div>
      </div>

      <div
        className="absolute inset-0 h-full w-full z-10 flex items-center justify-center overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
      >
        <div className="absolute inset-0 bg-white" />
        {before && (
          <img
            draggable="false"
            src={before}
            alt="Before"
            className="h-full w-full object-contain p-1 opacity-100 relative z-10"
          />
        )}
        <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/60 text-white text-[10px] font-black rounded-full uppercase tracking-widest z-20 backdrop-blur-md">Before</div>
      </div>

      <div
        className="absolute inset-y-0 w-1 bg-white z-20 pointer-events-none"
        style={{ left: `${sliderPos}%` }}
      >
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-[0_0_40px_rgba(0,0,0,0.1)] border">
          <div className="flex gap-1">
            <div className="w-1 h-4 bg-primary/20 rounded-full" />
            <div className="w-1 h-4 bg-primary/20 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageSpot({ image, onDelete, onReprocess }: { image: ImageFile; onDelete: (id: number) => void; onReprocess: (id: number) => void }) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [originalUrl, setOriginalUrl] = useState("");
  const [processedUrl, setProcessedUrl] = useState("");
  const [customProcessedUrl, setCustomProcessedUrl] = useState("");

  useEffect(() => {
    const url = URL.createObjectURL(image.file);
    setOriginalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image.file]);

  useEffect(() => {
    if (image.processedFile) {
      const url = URL.createObjectURL(image.processedFile);
      setProcessedUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setProcessedUrl("");
    }
  }, [image.processedFile]);

  const isProcessing = !image.processedFile;

  return (
    <Card className="group relative overflow-hidden border bg-card/50 backdrop-blur transition-all duration-500 hover:shadow-2xl hover:border-primary/20">
      <CardContent className="p-0">
        <div className="relative aspect-square overflow-hidden bg-muted/10 flex items-center justify-center">
          {isProcessing ? (
            <div className="flex flex-col items-center gap-4 z-10 animate-in fade-in zoom-in duration-500">
              <div className="relative">
                <Loader2 className="h-10 w-10 animate-spin text-primary/40" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-2 w-2 bg-primary rounded-full animate-pulse" />
                </div>
              </div>
            </div>
          ) : (
            <CompareSlider before={originalUrl} after={customProcessedUrl || processedUrl} />
          )}
        </div>
      </CardContent>

      {!isProcessing && (
        <CardFooter className="flex flex-col gap-3 p-3 border-t bg-muted/5">
          <div className="flex w-full items-center justify-between">
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => setIsEditModalOpen(true)} className="h-8 w-8 border-muted-foreground/10 hover:bg-primary/5 hover:text-primary transition-colors">
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => onReprocess(image.id)} className="h-8 w-8 border-muted-foreground/10 hover:bg-primary/5 hover:text-primary transition-colors" title="Process Again">
                <RefreshCcw className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => onDelete(image.id)} className="h-8 w-8 border-muted-foreground/10 hover:bg-destructive/5 hover:text-destructive transition-colors">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Button variant="default" size="sm" asChild className="h-8 text-[10px] font-bold px-4 shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 active:translate-y-0">
              <a href={customProcessedUrl || processedUrl} download={`processed-${image.id}.png`}>
                <Download className="h-3.5 w-3.5 mr-2" /> Download
              </a>
            </Button>
          </div>
        </CardFooter>
      )}

      <EditModal
        image={image}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={(url) => setCustomProcessedUrl(url)}
      />
    </Card>
  );
}
